import { ReactNode, useMemo, useState } from 'react';
import {
  useInstantSearch,
  usePagination,
  useSortBy,
} from 'react-instantsearch';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { Text16 } from '@sorare/core/src/atoms/typography';
import Dialog from '@sorare/core/src/components/dialog';
import { FilterSection } from '@sorare/core/src/components/search/FilterSection';
import { Option } from '@sorare/core/src/components/search/Option';
import {
  AlgoliaCardIndexesName,
  AlgoliaCardIndexesNames,
  useConfigContext,
} from '@sorare/core/src/contexts/config';
import { useMarketplaceLifecycle } from '@sorare/core/src/hooks/useMarketplaceLifecycle';
import { FilterWidget, FiltersGroup } from '@sorare/core/src/lib/filters';
import { filters, sorts as sortsMessages } from '@sorare/core/src/lib/glossary';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import { useClearAllFilters } from 'search/ClearAllFilters';
import useFiltersCount from 'search/FiltersManager/useFiltersCount';

import { MobileFilters } from './MobileFilters';

type Props = {
  sorts: AlgoliaCardIndexesNames;
  open: boolean;
  onClose: () => void;
  filtersGroups: FiltersGroup[];
  children?: ReactNode;
};

const messages = defineMessages({
  title: {
    id: 'FiltersManagersDialog.title',
    defaultMessage: 'Filters',
  },
});

const Body = styled(Vertical).attrs({ gap: 2 })`
  padding: var(--triple-unit) var(--double-unit);
`;

const ButtonsWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--unit);
  padding: var(--unit);

  @media ${tabletAndAbove} {
    flex-direction: row;
  }
`;

export const FiltersManagerDialog = ({
  sorts,
  open,
  onClose: onCloseDialog,
  filtersGroups,
  children,
}: Props) => {
  const [activeWidget, setActiveWidget] = useState<FilterWidget>();
  const clearAllFilters = useClearAllFilters();
  const filtersCount = useFiltersCount();
  const { results } = useInstantSearch();
  const { algoliaCardIndexes } = useConfigContext();
  const { formatMessage } = useIntl();

  const sortItems = useMemo(
    () =>
      sorts.map(label => ({
        value: algoliaCardIndexes[label],
        label: formatMessage(sortsMessages[label]),
      })),
    [sorts, algoliaCardIndexes, formatMessage]
  );

  const { refine, currentRefinement } = useSortBy({ items: sortItems });
  const { refine: setPage } = usePagination();
  const { updateSort } = useMarketplaceLifecycle();

  const sortWidget: FilterWidget = useMemo(
    () => ({
      key: 'sort',
      type: 'sort',
      title: (
        <Text16 bold>
          <FormattedMessage {...filters.sortBy} />
        </Text16>
      ),
      Component: ({ visible }) => {
        if (!visible) return <FilterSection />;

        return (
          <div className="FilterWidget visible">
            {sortItems.map(sortItem => (
              <div key={sortItem.value}>
                <Option
                  label={sortItem.label}
                  active={currentRefinement === sortItem.value}
                  onClick={() => {
                    setPage(0);
                    refine(sortItem.value);
                    updateSort(sortItem.label as AlgoliaCardIndexesName);
                  }}
                  variant="radio"
                />
              </div>
            ))}
          </div>
        );
      },
    }),
    [currentRefinement, refine, setPage, sortItems, updateSort]
  );

  const groups = [
    {
      slug: 'sort',
      filters: [sortWidget],
    },
    ...(filtersGroups || []),
  ];

  const onClose = () => {
    setActiveWidget(undefined);
    onCloseDialog();
  };

  return (
    // keepMounted is needed so that Algolia keeps filtering even when filters are hidden
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      keepMounted
      onBack={() => (activeWidget ? setActiveWidget(undefined) : onClose())}
      title={
        <div className="text-center">
          {activeWidget ? (
            activeWidget.title
          ) : (
            <Text16 bold>
              <FormattedMessage {...messages.title} />
            </Text16>
          )}
        </div>
      }
      footer={
        <ButtonsWrapper>
          <Button
            color="red"
            size="medium"
            onClick={clearAllFilters}
            disabled={!filtersCount}
            fullWidth
          >
            <Text16>
              <FormattedMessage {...filters.clearAll} />
            </Text16>
          </Button>
          <Button onClick={onClose} size="medium" color="primary" fullWidth>
            <FormattedMessage
              id="FiltersManagerDialog.viewResults"
              defaultMessage="Show {results, number} results"
              values={{
                results: results ? results.nbHits : 0,
              }}
            />
          </Button>
        </ButtonsWrapper>
      }
    >
      <Body>
        {children}
        <MobileFilters
          filters={groups}
          onClick={(widget: FilterWidget) => setActiveWidget(widget)}
          activeWidget={activeWidget}
        />
      </Body>
    </Dialog>
  );
};

export default FiltersManagerDialog;
